import {useEffect} from 'react';
import {useStore} from 'react-redux';
import {initKnWeb} from '../../../../../../app/initKnWeb.function';
import {useThunkDispatch} from '../../../../../../common/useThunkDispatch.hook';
import useTypedDispatch from '../../../../../../common/useTypedDispatch';
import useTypedSelector from '../../../../../../common/useTypedSelector';
import {TalkViewerErrorType} from '../../../../../../errors/TalkViewerErrorType.enum';
import {TalkViewerData} from '../../../../../../graphql/generated/types';
import {AppStore} from '../../../../../../state/store';
import {useViewerInfoQuery} from '../../../../../../wrapper/viewerWrapper';
import {TALK_VIEWER_MIN_INTERFACE_VERSION} from '../constant/talkViewerConstant';
import {talkViewerErrorOccured} from '../state/talkViewerSlice';
import {setIsViewerEndExposed} from '../state/viewerEndSlice';
import {fetchTalkViewerContent} from '../util/fetchTalkViewerContent.thunk';

interface useFetchTalkViewerProps {
  productId: number;
  seriesId: number;
}

export function useFetchTalkViewer({
  productId,
  seriesId,
}: useFetchTalkViewerProps) {
  const {productInfo, isLoaded} = useTypedSelector(state => ({
    productInfo: state.talkViewer.productInfoMap[productId],
    isLoaded: state.talkViewer.loaded,
  }));

  const {data} = useViewerInfoQuery({seriesId, productId});
  const viewerInfo = data;
  const viewerData = viewerInfo?.viewerData as TalkViewerData;
  const decryptKey = viewerData?.talkDownloadData.dec ?? undefined;
  const talkViewerError = useTypedSelector(state => state.talkViewer.error);
  const dispatch = useTypedDispatch();
  const thunkDispatch = useThunkDispatch();
  const store = useStore();
  useEffect(() => {
    if (!viewerInfo) {
      return;
    }
    initKnWeb(store as AppStore);
    if (window?.KNApp?.getInterfaceVersion) {
      const appInterfaceVersion = Number(
        window?.KNApp?.getInterfaceVersion?.()
      );
      // 앱 인터페이스 버전이 최소버전 아래라면 강제업데이트를 띄우고 아무 동작하지 않습니다.
      if (appInterfaceVersion < TALK_VIEWER_MIN_INTERFACE_VERSION) {
        window?.KNApp?.needUpdate?.(true);
        dispatch(talkViewerErrorOccured(TalkViewerErrorType.NeedUpdate));
        return;
      }
    }
    window?.KNApp?.startViewerLoading?.();
    dispatch(setIsViewerEndExposed(false));
    thunkDispatch(
      fetchTalkViewerContent({
        productId,
        decryptKey,
        triggerType: 'page_move',
        viewerInfoData: viewerInfo,
      })
    );
  }, [decryptKey, dispatch, productId, store, thunkDispatch, viewerInfo]);

  if (talkViewerError) {
    return {
      error: talkViewerError,
    };
  }
  if (!productInfo || !isLoaded) {
    return {
      isLoading: true,
    };
  }

  return {
    isLoading: false,
    data: productInfo,
  };
}
